import * as React from 'react';

import { cn, TouchSelect, TouchSelectProps } from '@rematter/ui-web';
import { UseQueryResult } from '@tanstack/react-query';

import { useSearchTerm } from '~/shared/hooks/useSearchTerm';
import { QueryHookResult } from '~/shared/providers/api/createQueryHook';

export type TouchSelectOption = {
  key: string;
  id: string;
  label: string;
  subLabel?: string;
  searchable?: string;
  group?: string;
};

export type EntityTouchSelectProps<Entity> = Omit<
  TouchSelectProps<string>,
  'query' | 'onChangeQuery' | 'options' | 'isLoading' | 'value' | 'onChange'
> & {
  value: string | undefined | null;
  onChange: (value: string | undefined, entity?: Entity) => void;
  onEntityResolved?: (entity: Entity) => void;
  sortOptions?: (options: TouchSelectOption[]) => TouchSelectOption[];
  skipSearch?: boolean;
};

/**
 * Creates a TouchSelect component that searches entities remotely.
 * The search query is sent to the server via the useQueryHook.
 * Use this when you have a large dataset that needs server-side filtering.
 */
const createRemotelySearchedEntityTouchSelect =
  <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Q extends (...args: any[]) => UseQueryResult<T, unknown>,
    T extends { id: string }[],
  >(
    name: string,
    useQueryHook: Q,
    {
      formatLabel,
      formatSubLabel,
      formatSearchable,
      formatGroup,
    }: {
      formatLabel: (entity: QueryHookResult<Q>[number]) => string;
      formatSubLabel?: (entity: QueryHookResult<Q>[number]) => string;
      formatSearchable?: (entity: QueryHookResult<Q>[number]) => string;
      formatGroup?: (entity: QueryHookResult<Q>[number]) => string | undefined;
    },
  ) =>
  ({
    // Component-specific props
    onChange,
    onEntityResolved,
    skipSearch,
    value,
    sortOptions,
    onTriggerClick,

    // Explicitly extract valid TouchSelectProps to prevent DOM elements from
    // leaking into query params (refs, event handlers with captured DOM nodes, etc.)
    variant,
    color,
    placeholder,
    isDisabled,
    className,
    themeMode,
    prefix,
    onFocus,
    onBlur,
    'aria-label': ariaLabel,
    label,
    labelAdornment,
    isRequired,
    isInvalid,
    errorMessage,
    description,
    hideSearch,
    showChevron,
    renderEmpty,

    // Query-specific params (only serializable values should remain)
    ids,
    statuses,
    size,
    ...additionalQueryParams
  }: EntityTouchSelectProps<QueryHookResult<Q>[number]> & Parameters<Q>[0]) => {
    const [recentlySelectedEntity, setRecentlySelectedEntity] = React.useState<
      QueryHookResult<Q>[number] | undefined
    >(undefined);

    const onEntityResolvedRef = React.useRef(onEntityResolved);
    onEntityResolvedRef.current = onEntityResolved;

    const { searchTerm, setSearchTerm, debouncedSearchTerm } = useSearchTerm();

    // Reconstruct touchSelectProps from explicitly extracted values
    // This ensures no non-serializable values leak to query params
    const touchSelectProps = {
      variant,
      placeholder,
      isDisabled,
      className,
      themeMode,
      color,
      prefix,
      onFocus,
      onBlur,
      onTriggerClick,
      'aria-label': ariaLabel,
      label,
      labelAdornment,
      isRequired,
      isInvalid,
      errorMessage,
      description,
      size,
      hideSearch,
      showChevron,
      renderEmpty,
    };

    // Memoize search query params separately
    const searchQueryParams = React.useMemo(
      () => ({
        statuses,
        ids,
        searchTerm: debouncedSearchTerm,
        ...additionalQueryParams,
        queryOptions: {
          enabled: !skipSearch && !onTriggerClick,
        },
      }),
      [
        statuses,
        ids,
        debouncedSearchTerm,
        skipSearch,
        onTriggerClick,
        additionalQueryParams,
      ],
    );

    const { data: selectedEntities } = useQueryHook({
      ids: value ? [value] : ['invalid-id'],
      queryOptions: {
        enabled: !!value, // Only run query if value exists
      },
    } as never);

    React.useEffect(() => {
      if (!onEntityResolvedRef.current) return;
      const entity = selectedEntities?.at(0);
      if (entity && entity.id === value) {
        onEntityResolvedRef.current(entity as QueryHookResult<Q>[number]);
      }
    }, [selectedEntities, value]);

    const { data: entities, isFetching } = useQueryHook(
      searchQueryParams as never,
    );

    // Build options for TouchSelect
    const options = React.useMemo(() => {
      const mappedOptions =
        entities?.map((entity) => ({
          key: entity.id,
          id: entity.id,
          label: formatLabel(entity),
          subLabel: formatSubLabel?.(entity),
          searchable: formatSearchable?.(entity),
          group: formatGroup?.(entity),
        })) ?? [];

      return sortOptions ? sortOptions(mappedOptions) : mappedOptions;
    }, [entities, sortOptions]);

    // Normalize null to undefined for TouchSelect
    const normalizedValue = value ?? undefined;

    const selection = React.useMemo(() => {
      const selectedEntity =
        selectedEntities?.at(0)?.id === value
          ? selectedEntities?.at(0)
          : recentlySelectedEntity;

      return {
        label: selectedEntity ? formatLabel(selectedEntity) : '',
        subLabel:
          selectedEntity && formatSubLabel
            ? formatSubLabel(selectedEntity)
            : undefined,
      };
    }, [value, recentlySelectedEntity, selectedEntities]);

    const handleChange = React.useCallback(
      (values: string) => {
        const selectedEntity = entities?.find((entity) => entity.id === values);
        setRecentlySelectedEntity(selectedEntity);
        if (selectedEntity) {
          onEntityResolvedRef.current?.(selectedEntity);
        }
        onChange?.(selectedEntity?.id, selectedEntity);
      },
      [entities, onChange],
    );

    return (
      <TouchSelect<string>
        {...touchSelectProps}
        query={searchTerm}
        onChangeQuery={setSearchTerm}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={cn(`@${name}`, className)}
        options={options}
        isLoading={isFetching}
        value={normalizedValue}
        getSelection={() => selection}
        onChange={handleChange}
      />
    );
  };

export const createEntityTouchSelect = {
  remotelySearched: createRemotelySearchedEntityTouchSelect,
};
